// Minimal ISO-8601 duration support for touchpoint wait_time. Handles the
// day/hour/minute subset the journey builder uses (e.g. P2D, PT24H, PT30M).
const RE = /^P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?)?$/;

export function isoToDays(iso) {
  if (!iso) return 0;
  const m = RE.exec(iso);
  if (!m) return 0;
  const days = parseFloat(m[1] || 0);
  const hours = parseFloat(m[2] || 0);
  const mins = parseFloat(m[3] || 0);
  return days + hours / 24 + mins / (24 * 60);
}

export function isoToLabel(iso) {
  const d = isoToDays(iso);
  if (d === 0) return '0 delay';
  if (Number.isInteger(d)) return `${d} day${d === 1 ? '' : 's'}`;
  const hours = Math.round(d * 24);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

// Human wait-time label from an ISO-8601 duration: the two largest non-zero units,
// spelled out and pluralized (e.g. "1 day 12 hours", "30 minutes"). Normalizes
// overflow (PT36H → "1 day 12 hours", PT90M → "1 hour 30 minutes"). Returns '' for a
// zero/empty duration so callers can show "Sent immediately".
export function formatWaitLabel(iso) {
  if (!iso) return '';
  const m = RE.exec(iso);
  if (!m) return '';
  const totalMin = Math.round(parseFloat(m[1] || 0) * 24 * 60 + parseFloat(m[2] || 0) * 60 + parseFloat(m[3] || 0));
  if (totalMin <= 0) return '';
  const days = Math.floor(totalMin / (24 * 60));
  const hours = Math.floor((totalMin % (24 * 60)) / 60);
  const minutes = totalMin % 60;
  const parts = [[days, 'day'], [hours, 'hour'], [minutes, 'minute']]
    .filter(([n]) => n > 0)
    .slice(0, 2)
    .map(([n, unit]) => `${n} ${unit}${n === 1 ? '' : 's'}`);
  return parts.join(' ');
}

// Build an ISO-8601 duration from a value + unit for the wait-time input.
export function toIsoDuration(value, unit) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return unit === 'hours' ? `PT${n}H` : `P${n}D`;
}

// Parse an ISO-8601 duration into { value, unit } for the wait-time input.
// Falls back to days (via isoToDays) for shapes the simple form doesn't model.
export function parseWaitTime(iso) {
  if (!iso) return { value: 0, unit: 'days' };
  const h = /^PT(\d+)H$/.exec(iso);
  if (h) return { value: Number(h[1]), unit: 'hours' };
  const d = /^P(\d+)D$/.exec(iso);
  if (d) return { value: Number(d[1]), unit: 'days' };
  return { value: Math.round(isoToDays(iso)), unit: 'days' };
}
